var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@ui/atoms/Button';
import { Text } from '@ui/atoms/Text';
import { FileHandler } from '@ui/molecules/FileHandler';
import { InlineNotification } from '@ui/molecules/InlineNotification';
import { ProgressBar } from '@ui/molecules/ProgressBar';
import { translate } from '@utils/i18n';
import { presignUploads, confirmUploads } from '../../services/CustomerFileDropService';
import './UploadManagerStyles.scss';
const namespace = 'features.settings.customer_file_drop';
const UPLOAD_TIMEOUT_MS = 15 * 60 * 1000; // match Storage::UPLOAD_EXPIRES_IN
const UPLOAD_CONCURRENCY = 4;
function bytesToHuman(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function typeLabelFor(file) {
    var _a;
    if (file.type.startsWith('image/'))
        return 'Photo';
    const ext = file.name.includes('.') ? (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toUpperCase() : '';
    return ext || 'File';
}
function queueItemToHandlerItem(item) {
    return {
        id: item.id,
        name: item.file.name,
        typeLabel: typeLabelFor(item.file),
        meta: bytesToHuman(item.file.size),
        status: item.status,
        progress: item.progress,
        downloadUrl: item.downloadUrl
    };
}
function runWithConcurrency(items, concurrency, worker) {
    return __awaiter(this, void 0, void 0, function* () {
        let nextIndex = 0;
        const limit = Math.max(1, Math.min(concurrency, items.length || 1));
        const runners = Array.from({ length: Math.min(limit, items.length) }, () => __awaiter(this, void 0, void 0, function* () {
            while (nextIndex < items.length) {
                const current = nextIndex;
                nextIndex += 1;
                // Each runner must finish its assigned upload before taking another item.
                // eslint-disable-next-line no-await-in-loop
                yield worker(items[current], current);
            }
        }));
        yield Promise.all(runners);
    });
}
/**
 * Presigned S3 mass-upload manager.
 * Visual shell is Quimby FileHandler; transport stays folderId + presign/PUT/confirm.
 */
function UploadManager({ folderId, onUploadsComplete }) {
    const [items, setItems] = useState([]);
    const [isBatchRunning, setIsBatchRunning] = useState(false);
    const [confirmSucceeded, setConfirmSucceeded] = useState(false);
    const [confirmError, setConfirmError] = useState(null);
    // Track object URLs so they can be revoked on unmount.
    const objectUrlsRef = useRef([]);
    useEffect(() => {
        const urls = objectUrlsRef.current;
        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);
    const addFiles = (files) => {
        if (files.length === 0)
            return;
        const newItems = files.map((file) => {
            const downloadUrl = URL.createObjectURL(file);
            objectUrlsRef.current.push(downloadUrl);
            return {
                id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 9)}`,
                file,
                status: 'pending',
                progress: 0,
                downloadUrl
            };
        });
        setConfirmSucceeded(false);
        setConfirmError(null);
        setItems((prev) => [...prev, ...newItems]);
    };
    const updateItemStatus = (index, partial) => {
        setItems((prev) => prev.map((item, i) => (i === index ? Object.assign(Object.assign({}, item), partial) : item)));
    };
    const uploadSingle = (item, index, slot) => __awaiter(this, void 0, void 0, function* () {
        const s3Key = slot.s3Key || slot.s3_key;
        if (!(slot === null || slot === void 0 ? void 0 : slot.url) || !s3Key) {
            updateItemStatus(index, { status: 'error', error: 'Missing upload slot' });
            throw new Error('Missing upload slot');
        }
        updateItemStatus(index, { status: 'uploading', progress: 0 });
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.timeout = UPLOAD_TIMEOUT_MS;
            xhr.upload.onprogress = (ev) => {
                if (ev.lengthComputable) {
                    updateItemStatus(index, { progress: Math.round((ev.loaded / ev.total) * 100) });
                }
            };
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    updateItemStatus(index, { status: 'done', progress: 100, s3Key });
                    resolve();
                }
                else {
                    updateItemStatus(index, { status: 'error', error: `HTTP ${xhr.status}` });
                    reject(new Error(`Upload failed: ${xhr.status}`));
                }
            };
            xhr.onerror = () => {
                updateItemStatus(index, { status: 'error', error: 'Network error' });
                reject(new Error('Network error'));
            };
            xhr.ontimeout = () => {
                updateItemStatus(index, { status: 'error', error: 'Upload timed out' });
                reject(new Error('Upload timed out'));
            };
            xhr.open('PUT', slot.url, true);
            xhr.setRequestHeader('Content-Type', item.file.type || 'application/octet-stream');
            xhr.send(item.file);
        });
    });
    const handleUpload = () => __awaiter(this, void 0, void 0, function* () {
        const pendingItems = items
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.status === 'pending' || item.status === 'error');
        if (pendingItems.length === 0)
            return;
        setIsBatchRunning(true);
        setConfirmSucceeded(false);
        setConfirmError(null);
        try {
            const fileSpecs = pendingItems.map(({ item }) => ({
                filename: item.file.name,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                content_type: item.file.type || 'application/octet-stream',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                byte_size: item.file.size
            }));
            const { slots } = yield presignUploads(folderId, fileSpecs);
            // Capture s3 keys from slots now — React state updates from XHR are async and
            // must not be the only source of truth for confirm.
            const uploadedForConfirm = [];
            yield runWithConcurrency(pendingItems, UPLOAD_CONCURRENCY, (_a, i_1) => __awaiter(this, [_a, i_1], void 0, function* ({ item, index }, i) {
                try {
                    const slot = slots[i];
                    yield uploadSingle(item, index, slot);
                    const s3Key = (slot === null || slot === void 0 ? void 0 : slot.s3Key) || (slot === null || slot === void 0 ? void 0 : slot.s3_key);
                    if (s3Key) {
                        uploadedForConfirm.push({
                            index,
                            s3Key,
                            originalFilename: item.file.name,
                            contentType: item.file.type || 'application/octet-stream',
                            byteSize: item.file.size
                        });
                    }
                }
                catch (_b) {
                    // Per-file failure already recorded on the item; continue the pool.
                }
            }));
            if (uploadedForConfirm.length === 0) {
                setConfirmError(translate('upload_error', { namespace }));
                return;
            }
            yield confirmUploads(folderId, uploadedForConfirm.map(({ s3Key, originalFilename, contentType, byteSize }) => ({
                s3Key,
                originalFilename,
                contentType,
                byteSize
            })));
            setConfirmSucceeded(true);
            onUploadsComplete();
        }
        catch (_a) {
            setConfirmError(translate('upload_error', { namespace }));
            setConfirmSucceeded(false);
        }
        finally {
            setIsBatchRunning(false);
        }
    });
    const handleFilesSelected = useCallback((files) => {
        addFiles(files);
    }, []);
    const doneCount = items.filter((i) => i.status === 'done').length;
    const errorCount = items.filter((i) => i.status === 'error').length;
    const pendingCount = items.filter((i) => i.status === 'pending').length;
    const aggregatePct = items.length === 0 ? 0 : Math.round((doneCount / items.length) * 100);
    return (React.createElement("div", { className: "upload-manager", "data-testid": "upload-manager" },
        React.createElement(FileHandler, { hideEmptyState: true, disabled: isBatchRunning, onFilesSelected: handleFilesSelected, items: items.map(queueItemToHandlerItem), dropzoneHint: React.createElement("p", { className: "upload-manager__hint" },
                translate('upload_drop_hint', { namespace }),
                ' ',
                React.createElement("span", { className: "upload-manager__browse-btn", "data-testid": "browse-files-button" }, translate('upload_browse', { namespace }))), dropzoneNote: React.createElement("p", { className: "upload-manager__note" }, translate('upload_note', { namespace })) }),
        items.length > 0 && (React.createElement("div", { className: "upload-manager__list" },
            (isBatchRunning || doneCount > 0) && (React.createElement("div", { className: "upload-manager__aggregate" },
                React.createElement(Text, { variant: "x-small" }, translate('upload_progress', { namespace, completed: doneCount, total: items.length })),
                React.createElement(ProgressBar, { value: aggregatePct, showLabel: false }))),
            React.createElement("div", { className: "upload-manager__actions" },
                errorCount > 0 || confirmError ? (React.createElement(InlineNotification, { variant: "error", title: translate('upload_error', { namespace }), children: React.createElement("span", null, confirmError || translate('upload_error', { namespace })) })) : null,
                (pendingCount > 0 || errorCount > 0 || confirmError) && !isBatchRunning && (React.createElement(Button, { onClick: () => void handleUpload(), disabled: isBatchRunning }, errorCount > 0 || confirmError
                    ? translate('upload_retry', { namespace })
                    : translate('upload_files', { namespace }))),
                confirmSucceeded && !isBatchRunning ? (React.createElement(InlineNotification, { variant: "success", title: translate('upload_complete', { namespace }), children: React.createElement("span", null, translate('upload_complete', { namespace })) })) : null)))));
}
export default UploadManager;
